// ABOUTME: Application logger with level filtering. Use createLogger('ComponentName') for a scoped instance.
// ABOUTME: `error` also reports to Sentry (no-op without a DSN), so a logged failure is never invisible in production.
import * as Sentry from "@sentry/react"
import config from "../../config"

type LogLevel = "debug" | "info" | "warn" | "error"

const LOG_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

const currentLevel = LOG_PRIORITY[config.LOG_LEVEL as LogLevel]

const shouldLog = (level: LogLevel): boolean => LOG_PRIORITY[level] >= currentLevel

/**
 * Sends an error-level log line to Sentry: the first Error argument as the exception (its own stack, grouped
 * by Sentry as usual), the string arguments as the message, everything else as `extra`. Without an Error it
 * becomes a captureMessage. The SDK marks an Error object on its first capture and skips later captures of the
 * same object, so a site that captures with richer context itself (ErrorBoundary) must do so BEFORE logging.
 * Global handlers (window error / unhandledrejection) must not log through here at all: Sentry's globalHandlers
 * integration already reports them with the original reason, which index.tsx's beforeSend filters rely on.
 */
const reportToSentry = (scope: string | null, args: unknown[]): void => {
  const error = args.find((arg): arg is Error => arg instanceof Error)
  const message = args.filter((arg): arg is string => typeof arg === "string").join(" ")
  const extra: Record<string, unknown> = {}
  args.forEach((arg, index) => {
    if (arg === error || typeof arg === "string") return
    extra[`arg${index}`] = arg
  })
  const tags = scope ? { logger: scope } : undefined
  if (error) {
    Sentry.captureException(error, { tags, extra: { message: message || undefined, ...extra } })
  } else {
    Sentry.captureMessage(message || "logger.error without a message", { level: "error", tags, extra })
  }
}

/* eslint-disable no-console */
export const addLogDebug = (...args: unknown[]): void => {
  if (shouldLog("debug")) console.debug(...args)
}

export const addLogInfo = (...args: unknown[]): void => {
  if (shouldLog("info")) console.info(...args)
}

export const addLogWarn = (...args: unknown[]): void => {
  if (shouldLog("warn")) console.warn(...args)
}

export const addLogError = (...args: unknown[]): void => {
  console.error(...args)
  reportToSentry(null, args)
}
/* eslint-enable no-console */

export interface Logger {
  debug: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
}

export const createLogger = (scope: string): Logger => ({
  debug: (...args) => addLogDebug(`[${scope}]`, ...args),
  info: (...args) => addLogInfo(`[${scope}]`, ...args),
  warn: (...args) => addLogWarn(`[${scope}]`, ...args),
  error: (...args) => {
    // eslint-disable-next-line no-console -- the logger is the one place console output is allowed
    console.error(`[${scope}]`, ...args)
    reportToSentry(scope, args)
  },
})
